/**
 * AlertDetails Component
 * 
 * Fifth step in the AlertForm wizard that allows users to configure the alert details
 * such as name, description, and severity.
 */

import React, { useState, useEffect } from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { 
  AlertSeverity,
  getSeverityDisplayText,
  getSeverityIcon
} from '../../../models/AlertModels';

/**
 * AlertDetails component for configuring alert details
 * 
 * @returns {JSX.Element} The AlertDetails component
 */
function AlertDetails() {
  const { formState, updateFormState } = useAlertContext();
  const [validationErrors, setValidationErrors] = useState({});
  // Add null check to prevent crash when formState.alert is null
  const [nameInputValue, setNameInputValue] = useState(formState?.alert?.name || '');
  
  // Define a validation function - we'll call this inside useEffect
  const validateDetails = () => {
    // If formState or formState.alert is null, we can't validate
    if (!formState || !formState.alert) return false;
    
    const errors = {};
    let isValid = true;
    
    // Validate name
    if (!formState.alert.name || formState.alert.name.trim() === '') {
      errors.name = 'Alert name is required';
      isValid = false;
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formState.alert.name)) {
      errors.name = 'Alert name can only contain letters, numbers, underscores, and dashes';
      isValid = false;
    }
    
    // Validate severity
    if (!formState.alert.severity) {
      errors.severity = 'Severity is required';
      isValid = false;
    }
    
    setValidationErrors(errors);
    return isValid;
  };
  
  // Place all useEffect hooks before any conditional returns
  // Validate details when component mounts or relevant fields change
  useEffect(() => {
    if (formState?.alert) {
      validateDetails();
    }
  }, [formState?.alert, formState?.alert?.name, formState?.alert?.severity, validateDetails]);
  
  // Initialize nameInputValue when component mounts or form state changes
  useEffect(() => {
    if (formState?.alert?.name !== undefined) {
      setNameInputValue(formState.alert.name || '');
    }
  }, [formState?.alert?.name]);
  
  // Handle cases where formState or formState.alert might be null
  if (!formState || !formState.alert) {
    // Return a loading state or redirect back to the first step
    return (
      <div className="alert-details-container">
        <h3 className="step-title">Loading Alert Details...</h3>
        <p>Please wait while we load the alert details.</p>
      </div>
    );
  }
  
  // Check if we're in edit mode - prevent changing alert name in edit mode
  const isEditMode = formState.isEditing;
  
  // Regular expression for validating alert name - only alphanumeric, underscores, and dashes
  const nameRegex = /^[a-zA-Z0-9_-]+$/
  
  /**
   * Update a specific field in the alert
   * 
   * @param {string} field - The field to update
   * @param {any} value - The new value
   */
  const updateField = (field, value) => {
    // Special handling for name field to validate format
    if (field === 'name') {
      setNameInputValue(value);
      
      // Only update the form state if the name is valid or empty
      if (value === '' || nameRegex.test(value)) {
        updateFormState({
          alert: {
            ...formState.alert,
            [field]: value
          }
        });
        
        // Clear validation error
        if (validationErrors[field]) {
          const updatedErrors = { ...validationErrors };
          delete updatedErrors[field];
          setValidationErrors(updatedErrors);
        }
      } else {
        // Set validation error for invalid format
        setValidationErrors({
          ...validationErrors,
          name: 'Alert name can only contain letters, numbers, underscores, and dashes'
        });
      }
    } else {
      // Normal handling for other fields
      updateFormState({
        alert: {
          ...formState.alert,
          [field]: value
        }
      });
      
      // Clear validation error for this field if it exists
      if (validationErrors[field]) {
        const updatedErrors = { ...validationErrors };
        delete updatedErrors[field];
        setValidationErrors(updatedErrors);
      }
    }
  };
  
  // Validate details function is now defined at the top of the component
  
  return (
    <div className="alert-details-container">
      <h3 className="step-title">Configure Alert Details</h3>
      <p className="step-description">
        {isEditMode
          ? 'Edit the description and severity of your alert. Alert name cannot be changed.'
          : 'Provide a name, description, and severity level for your alert.'}
      </p>
      
      {isEditMode && (
        <div className="edit-mode-notice" style={{
          background: 'rgba(0, 0, 0, 0.05)', 
          padding: '10px 15px',
          borderRadius: '4px',
          marginBottom: '15px',
          borderLeft: '4px solid #4a90e2',
          color: '#666'
        }}>
          <i className="fa fa-info-circle" style={{ marginRight: '8px' }}></i>
          Alert name cannot be changed after creation. Other properties can still be modified.
        </div>
      )}
      
      <div className="alert-details-form">
        <div className="form-group">
          <label htmlFor="alert-name" className="form-label">Alert Name <span className="required">*</span></label>
          <input
            id="alert-name"
            type="text"
            className={`form-input ${validationErrors.name ? 'error' : ''} ${isEditMode ? 'disabled' : ''}`}
            value={nameInputValue}
            onChange={(e) => !isEditMode && updateField('name', e.target.value)}
            placeholder="Enter a descriptive name for this alert (letters, numbers, _, -)"
            disabled={isEditMode}
            style={isEditMode ? { 
              backgroundColor: 'rgba(240, 240, 240, 0.5)', 
              cursor: 'not-allowed',
              color: '#444',
              fontWeight: 'bold',
              border: '1px solid #ccc'
            } : {}}
          />
          {validationErrors.name && (
            <div className="form-error">{validationErrors.name}</div>
          )}
          <div className="form-help-text">
            Alert name can only contain letters, numbers, underscores (_), and dashes (-). No spaces or special characters allowed.
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="alert-description" className="form-label">Description</label>
          <textarea
            id="alert-description"
            className="form-textarea"
            value={formState.alert.description || ''}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Enter a detailed description of this alert (optional)"
            rows={4}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Severity <span className="required">*</span></label>
          <div className="severity-options">
            {Object.values(AlertSeverity).map((severity) => (
              <div 
                key={severity}
                className={`severity-option ${formState.alert.severity === severity ? 'selected' : ''} severity-${severity.toLowerCase()}`}
                onClick={() => updateField('severity', severity)}
              >
                <div className="severity-icon">
                  <i className={`fa fa-${getSeverityIcon(severity)}`}></i>
                </div>
                <div className="severity-content">
                  <h4 className="severity-title">{getSeverityDisplayText(severity)}</h4>
                  <p className="severity-description">
                    {severity === AlertSeverity.INFO && 'Informational alerts for non-critical events.'}
                    {severity === AlertSeverity.WARNING && 'Warning alerts for potential issues that need attention.'}
                    {severity === AlertSeverity.CRITICAL && 'Critical alerts for urgent issues that require immediate action.'}
                  </p>
                </div>
                <div className="severity-radio">
                  <input 
                    type="radio" 
                    checked={formState.alert.severity === severity}
                    onChange={() => {}} // Handled by onClick on parent div
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            ))}
          </div>
          {validationErrors.severity && (
            <div className="form-error">{validationErrors.severity}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AlertDetails;
